import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { APPLICATION_PATHS, ENTITLEMENTS } from '../../application-core/constants';
import ResourceVisualisationDialog from '../../application-core/visualisation/ResourceVisualisationDialog';
import DataOptions from '../../core/DataOptions';
import HistoryViewer from '../../core/HistoryViewer';
import CollectionIcon from '../../core/icons/Collection';
import Information from '../../core/Information';
import Positioning from '../../core/Positioning';
import ProjectImportContainer from '../../core/project/ProjectImportContainer';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { UserConsumer } from '../../core/providers/UserProvider';
import { globalStyles as styles } from '../../core/style/gstheme';
import TripleViewer from '../../core/TripleViewer';
import { ENTITLEMENTS as APPENTITLEMENTS, STATUS_ACCEPTED } from '../../core/utils/constants';
import withGS from '../../core/withGShoc';
import GS from '../../grafsync/grafsync';
import ResourceSummaryDetail from '../../ontology/ResourceSummaryDetail';
import { SKOS_OBJECT_CONFIGURATION } from '../constants';
import CollectionSelectorDialog from './CollectionSelectorDialog';
import ConceptDetail from './ConceptDetail';
import MergeDialog from './MergeDialog';
import ConceptExport from './ConceptExport';
import SuggestionsManager from './SuggestionsManager';

const APPROVED = STATUS_ACCEPTED;

const CLASSES = {
  "Concept": {
  },
  "ConceptScheme": {
  },
  "Resource": {
  }
};

const baseLink = APPLICATION_PATHS.TAXONOMY_MANAGER + '/project/reload';

class ConceptData extends Component {

  constructor(props) {
    super(props);
    this.state = {
      view: 0,
      pendingSuggestionCount: 0,
      suggestionItems: {},
      scroll: false,
      conceptIRI: null
    };
    this.conceptDataRef = React.createRef();
  }

  static getDerivedStateFromProps(props, state) {
    // For a change of concept scroll back to top
    if (props.conceptIRI !== state.conceptIRI) {
      return {
        conceptIRI: props.conceptIRI,
        scroll: true
      };
    }
    return null;
  }

  componentDidMount() {
    if (this.props.data.pendingDeletion) return;
    this.conceptDataRef.current.scrollIntoView({
      block: 'center',
      inline: 'center',
    });
    this.setState({ scroll: false });
  }

  componentDidUpdate() {
    if (this.state.scroll && this.conceptDataRef && this.conceptDataRef.current && !this.props.data.pendingDeletion) {
      this.conceptDataRef.current.scrollIntoView({
        block: 'center',
        inline: 'center',
      });
      this.setState({ scroll: false });
    }
  }

  addToCollection = (collectionIRI) => {
    // Collection will already have been loaded in order to select it so no delay caused
    GS.loadData([collectionIRI], {}, this.props.graph).then(() => {
      // We check that somebody else hasn't added it while we've been looking!
      const collection = GS.data(collectionIRI, this.props.graph);
      const memberProp = collection.type.includes("OrderedCollection") ? "memberList" : "member";
      if (!collection[memberProp] || (collection[memberProp] && !collection[memberProp].includes(this.props.conceptIRI))) {
        if (memberProp === "memberList") {
          GS.insert(collectionIRI, this.props.graph, memberProp, this.props.conceptIRI);
        }
        else {
          GS.addProperty(collectionIRI, this.props.graph, memberProp, this.props.conceptIRI);
        }
      }
    });
  };

  changeView = (index) => this.setState({ view: index });

  monitorSuggestionStatus = (pendingSuggestionCount, suggestionItems) => this.setState({ pendingSuggestionCount, suggestionItems });

  render() {

    const { graph, schemeIRI, isReadOnly, conceptIRI, onDeleteConcept, showFloatingLabel, defaultClasses, useWorkflow, projectUsers,
      hiddenProps, propertyTemplates, linkedData, iriOptions, showLabelDetail, project, onFoundConcept, taxonomies, ontologyProjects,
      classes, data, viewLang, useSlashIRI, useUuidIRI, move, entitlements, useXL, associatedProject, annotate, resourcesForNotes,
      enableTSV, sortNumerically, sortProperty, flat, useCounterIRI, counters } = this.props;
    const { view, pendingSuggestionCount, suggestionItems } = this.state;
    const isDeleted = data.pendingDeletion || data._notAvailable;
    const includeSuggestions = entitlements.includes(ENTITLEMENTS.VIEW_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MAKE_SUGGESTIONS) || entitlements.includes(ENTITLEMENTS.MANAGE_SUGGESTIONS);
    const includeExport = !isDeleted;
    // Import is a subscription level entitlement so we get subscription level entitlements rather than project level
    const includeImport = !isDeleted && !data.deprecated && !useCounterIRI && schemeIRI;
    const includePositioning = !isDeleted && !flat && schemeIRI !== null && schemeIRI !== "";
    const showCollections = entitlements.includes(ENTITLEMENTS.EDIT_TAXONOMY);
    const showMerge = entitlements.includes(ENTITLEMENTS.EDIT_TAXONOMY);
    const showOntologyProjects = entitlements.includes(ENTITLEMENTS.VIEW_ONTOLOGY_PROJECT) || entitlements.includes(ENTITLEMENTS.EDIT_ONTOLOGY_PROJECT);
    const isApproved = (useWorkflow && "status" in data && data.status[0]) === APPROVED ? true : false;

    return (
      <UserConsumer>
        {({ entitlements: appEntitlements }) =>
          <ProjectConsumer>
            {({ defaultLang, skosXL, hasComponent }) => (
              <div ref={this.conceptDataRef}>
                <DataOptions
                  handleNotAvailable={true}
                  useWorkflow={useWorkflow}
                  id={conceptIRI}
                  graph={graph}
                  changeView={this.changeView}
                  view={this.state.view}
                  historyOption={true}
                  exportOption={includeExport}
                  importOption={includeImport && appEntitlements.includes(APPENTITLEMENTS.CAN_IMPORT)}
                  annotationOption={true}
                  positioningOption={includePositioning}
                  suggestionOption={includeSuggestions}
                  suggestionCount={pendingSuggestionCount}
                />
                <div style={{ float: 'right', width: 'calc(100% - 60px)', marginBottom: '20px', paddingLeft: '10px' }}>
                  <ResourceSummaryDetail
                    handleNotAvailable={true}
                    baseIRI={schemeIRI}
                    project={project}
                    useWorkflow={useWorkflow}
                    allowIRIchange={schemeIRI !== undefined}
                    isReadOnly={isReadOnly || isApproved || data.pendingDeletion ? true : false}
                    graph={graph}
                    additionalViews={
                      <ResourceVisualisationDialog
                        defaultLang={defaultLang}
                        ontologyProjects={ontologyProjects}
                        viewLang={viewLang}
                        associatedProject={associatedProject}
                        objectPropertyConfiguration={SKOS_OBJECT_CONFIGURATION}
                        displayClasses={CLASSES}
                        sourceNode={conceptIRI}
                        graph={graph} />
                    }
                    additionalActions={(!isReadOnly && !isDeleted && schemeIRI) ?
                      <span>
                        {showMerge && !isReadOnly && !isDeleted && !isApproved &&
                          <span style={{ marginLeft: 6 }}>
                            <MergeDialog
                              projectIRI={project}
                              schemeIRI={schemeIRI}
                              conceptIRI={conceptIRI}
                              graph={graph}
                              iriOptions={iriOptions}
                              useSlashIRI={useSlashIRI}
                              useUuidIRI={useUuidIRI}
                              useCounterIRI={useCounterIRI}
                              counters={counters}
                              useWorkflow={useWorkflow}
                              defaultLang={defaultLang} />
                          </span>
                        }
                        {showCollections &&
                          <span style={{ marginLeft: 6 }}>
                            <CollectionSelectorDialog button=
                              {
                                <Tooltip placement="right" title='Add to collection'>
                                  <IconButton
                                    aria-label="Add to collection"
                                    data-test="add-to-collection"
                                    size="small"
                                    className={classes.addButton}>
                                    <CollectionIcon />
                                  </IconButton>
                                </Tooltip>
                              }
                              projectIRI={project}
                              conceptIRI={conceptIRI}
                              graph={graph}
                              defaultLang={defaultLang}
                              onSelectCollection={this.addToCollection} />
                          </span>
                        }
                      </span>
                      :
                      null
                    }
                    defaultLang={defaultLang}
                    id={conceptIRI}
                    name="prefLabel"
                    viewLang={viewLang}
                    canDeprecate={true}
                    onDeleteResource={onDeleteConcept}
                    permission={ENTITLEMENTS.EDIT_TAXONOMY} />
                  {view === 0 &&
                    <ConceptDetail
                      move={move}
                      flat={flat}
                      useWorkflow={useWorkflow}
                      handleNotAvailable={true}
                      defaultLang={defaultLang}
                      annotate={annotate}
                      viewLang={viewLang}
                      suggestionItems={suggestionItems}
                      entitlements={entitlements}
                      project={project}
                      projectUsers={projectUsers}
                      hiddenProps={hiddenProps}
                      showOntologyProjects={showOntologyProjects}
                      onDeleteConcept={onDeleteConcept}
                      schemeIRI={schemeIRI}
                      useSlashIRI={useSlashIRI}
                      useUuidIRI={useUuidIRI}
                      useCounterIRI={useCounterIRI}
                      counters={counters}
                      useXL={useXL}
                      resourcesForNotes={resourcesForNotes}
                      iriOptions={iriOptions}
                      linkedData={linkedData}
                      associatedProject={associatedProject}
                      propertyTemplates={propertyTemplates}
                      showFloatingLabel={showFloatingLabel}
                      showLabelDetail={showLabelDetail}
                      graph={graph}
                      id={conceptIRI} />}
                  {view === 1 &&
                    <TripleViewer
                      handleNotAvailable={true}
                      requiredEntitlement={ENTITLEMENTS.EDIT_TAXONOMY}
                      isReadOnly={isReadOnly}
                      useWorkflow={useWorkflow}
                      id={conceptIRI}
                      graph={graph} />}
                  {view === 4 &&
                    <HistoryViewer
                      handleNotAvailable={true}
                      id={conceptIRI}
                      graph={graph} />}
                  <SuggestionsManager
                    schemeIRI={schemeIRI}
                    projectIRI={project}
                    flat={flat}
                    components={hasComponent}
                    projectUsers={projectUsers}
                    optionForXL={skosXL}
                    useWorkflow={useWorkflow}
                    isVisible={view === 5}
                    isReadOnly={isReadOnly}
                    useSlashIRI={useSlashIRI}
                    useUuidIRI={useUuidIRI}
                    useCounterIRI={useCounterIRI}
                    counters={counters}
                    iriOptions={iriOptions}
                    useXL={useXL}
                    defaultClasses={defaultClasses}
                    entitlements={entitlements}
                    defaultLang={defaultLang}
                    monitorSuggestionStatus={this.monitorSuggestionStatus}
                    handleNotAvailable={true}
                    id={conceptIRI}
                    graph={graph} />
                  {view === 6 &&
                    <ConceptExport
                      id={conceptIRI}
                      annotations={annotate}
                      sortProperty={sortProperty}
                      enableTSV={enableTSV}
                      templateTarget={schemeIRI}
                      sortNumerically={sortNumerically}
                      defaultLang={defaultLang}
                      examples={data.narrower ? data.narrower.slice(0, 3) : []}
                      entitlements={entitlements}
                      handleNotAvailable={true}
                      graph={graph}
                    />
                  }
                  {view === 7 && GS.outstandingEdits() === 0 &&
                    <div>
                      <Typography paragraph className={classes.mainHeading} style={{ marginTop: 10 }}>Merge Data</Typography>
                      <Typography paragraph variant="body2" color="textPrimary">This will merge data onto this concept. We recommend running a quality check after import.</Typography>
                      {schemeIRI ?
                        <ProjectImportContainer
                          baseLink={baseLink}
                          center={true}
                          spreadsheetOnly={true}
                          disableScreen={true}
                          includeOptions={false}
                          configOptions="taxonomy"
                          existingComponents={[].concat(taxonomies)}
                          xrefComponents={taxonomies}
                          useSlashIRI={useSlashIRI}
                          useUuidIRI={useUuidIRI}
                          useOptions={{
                            baseIRI: schemeIRI,
                            lang: defaultLang,
                            useXL: useXL,
                            removeSpaces: iriOptions.removeSpaces,
                            replaceSpaces: iriOptions.replaceSpaces,
                            removeSpecialCharacters: iriOptions.removeSpecialCharacters,
                            replaceSpecialCharacters: iriOptions.replaceSpecialCharacters,
                            minimiseEncoding: iriOptions.minimiseEncoding,
                            prependText: iriOptions.xlOptions.prependText,
                            appendText: iriOptions.xlOptions.shouldAppendText ? defaultLang : ""
                          }}
                          subLink={"taxonomy=" + schemeIRI + "&view=concept&concept=" + conceptIRI}
                          importContext={{
                            situation: "AttachToConcept",
                            resourceIRI: conceptIRI,
                            baseIRI: schemeIRI
                          }}
                          graph={graph}
                          id={project} />
                        :
                        <Information text="Data merge is not available." dataTest="floating-merge-not-editable" />
                      }
                    </div>
                  }
                  {view === 8 &&
                    <Positioning
                      id={conceptIRI}
                      displayType="Concept"
                      components={hasComponent}
                      handleNotAvailable={true}
                      onFoundResource={onFoundConcept}
                      componentIRI={schemeIRI}
                      projectIRI={project}
                      path="http://www.w3.org/2004/02/skos/core#broader"
                      defaultLang={defaultLang}
                      viewLang={viewLang}
                      graph={graph} />
                  }
                </div>
              </div>
            )}
          </ProjectConsumer>
        }
      </UserConsumer>
    );
  }
}

ConceptData.defaultProps = {
  isReadOnly: false,
  move: false,
  enableTSV: false,
  flat: false,
  hiddenProps: []
};

ConceptData.propTypes = {
  move: PropTypes.bool,
  id: PropTypes.string,
  data: PropTypes.object,
  entitlements: PropTypes.array.isRequired,
  project: PropTypes.string.isRequired,
  graph: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  enableTSV: PropTypes.bool,
  flat: PropTypes.bool,
  hiddenProps: PropTypes.array,
  schemeIRI: PropTypes.string,
  showFloatingLabel: PropTypes.func.isRequired,
  showLabelDetail: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useCounterIRI: PropTypes.bool,
  counters: PropTypes.object,
  useXL: PropTypes.bool,
  resourcesForNotes: PropTypes.bool,
  projectUsers: PropTypes.array,
  useWorkflow: PropTypes.bool,
  ontologyProjects: PropTypes.array,
  onFoundConcept: PropTypes.func,
  taxonomies: PropTypes.array,
  linkedData: PropTypes.array,
  associatedProject: PropTypes.array,
  annotate: PropTypes.bool,
  defaultClasses: PropTypes.array,
  sortProperty: PropTypes.string,
  sortNumerically: PropTypes.bool,
  conceptIRI: PropTypes.string.isRequired,
  onDeleteConcept: PropTypes.func.isRequired,
  propertyTemplates: PropTypes.array,
  classes: PropTypes.object,
  iriOptions: PropTypes.object
};

export default withStyles(styles)(withGS(ConceptData, ["ALL"], null, {}, true));